import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { X, Clock, DollarSign, AlertTriangle } from 'lucide-react';
import { CTAButton } from './shared/CTAButton';
export function PainPoints() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <section className="py-20 bg-white relative" data-id="element-94">
      <div className="max-w-5xl mx-auto px-4" data-id="element-95">
        {/* Part 1: The Struggle */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} className="mb-20" data-id="element-96">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E] mb-8 text-center" data-id="element-97">
            Si ya trabajás de manicurista, sabés algo que las “principiantes”
            todavía no entienden:
          </h2>

          <div className="bg-[#FFF8F0] p-8 md:p-10 rounded-2xl border-l-4 border-[#9B2C5E] shadow-sm" data-id="element-98">
            <p className="text-xl text-[#9B2C5E] font-serif italic mb-6 text-center" data-id="element-99">
              No se trata de hacer uñas. Se trata de que tus sets se vean{' '}
              <strong className="not-italic font-bold" data-id="element-100">consistentes</strong>.
            </p>

            <ul className="space-y-4" data-id="element-101">
              {['Un set te sale precioso… y el siguiente “más o menos”', 'La cutícula te queda prolija… pero no siempre', 'La estructura no se ve igual desde todos los ángulos', 'Aparecen levantamientos y te comen la agenda con retoques', 'Tardás demasiado, te agotás, y al final sentís que estás cobrando poco'].map((item, idx) => <motion.li key={idx} variants={itemVariants} className="flex items-start gap-3" data-id="element-102">
                  <div className="mt-1 min-w-[24px] h-6 flex items-center justify-center bg-red-100 rounded-full" data-id="element-103">
                    <X className="w-4 h-4 text-red-500" data-id="element-104" />
                  </div>
                  <span className="text-gray-700 text-lg" data-id="element-105">{item}</span>
                </motion.li>)}
            </ul>

            <div className="mt-8 pt-6 border-t border-[#C9A96E]/20" data-id="element-106">
              <p className="text-gray-800 font-medium text-lg" data-id="element-107">
                Y ahí aparece el bloqueo: querés subir precios, querés
                posicionarte mejor, querés que tus fotos se vean “nivel
                estudio”… pero te falta un método técnico que puedas repetir.
              </p>
            </div>
          </div>

          <div className="mt-10 text-center" data-id="element-108">
            <CTAButton text="SÍ, QUIERO MEJORAR MI TERMINACIÓN" variant="secondary" data-id="element-109" />
          </div>
        </motion.div>

        {/* Part 2: Hidden Costs */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} className="mb-20" data-id="element-110">
          <h3 className="text-2xl md:text-3xl font-bold text-center mb-10" data-id="element-111">
            El costo oculto de seguir “a prueba y error”
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-112">
            {[{
            icon: <Clock className="w-8 h-8 text-[#9B2C5E]" data-id="element-113" />,
            title: 'Tiempo',
            desc: 'Se te alargan los turnos, no sabés cuánto vas a tardar y terminás corriendo.'
          }, {
            icon: <DollarSign className="w-8 h-8 text-[#9B2C5E]" data-id="element-114" />,
            title: 'Plata',
            desc: 'Si tardás más, ganás menos por hora (aunque tu lista de precios diga otra cosa).'
          }, {
            icon: <AlertTriangle className="w-8 h-8 text-[#9B2C5E]" data-id="element-115" />,
            title: 'Reputación',
            desc: 'Cuando algo se levanta antes de tiempo, te genera estrés… y te puede costar una clienta.'
          }].map((card, idx) => <motion.div key={idx} variants={itemVariants} className="bg-white p-6 rounded-xl border border-gray-100 shadow-lg shadow-gray-100 hover:shadow-xl transition-shadow" data-id="element-116">
                <div className="mb-4 bg-[#F5E6E0] w-14 h-14 rounded-full flex items-center justify-center" data-id="element-117">
                  {card.icon}
                </div>
                <h4 className="text-xl font-bold mb-3 text-[#1A1A2E]" data-id="element-118">
                  {card.title}
                </h4>
                <p className="text-gray-600" data-id="element-119">{card.desc}</p>
              </motion.div>)}
          </div>

          <p className="text-center mt-8 text-gray-600 italic" data-id="element-120">
            Y no, esto no se arregla mirando tutoriales sueltos. Los tutoriales
            te dan “ideas”. Pero no te dan criterio. No te dan procedimiento. No
            te dan estándar.
          </p>
        </motion.div>

        {/* Part 3: The Truth */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} className="bg-[#1A1A2E] text-white p-8 md:p-12 rounded-3xl relative overflow-hidden" data-id="element-121">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#9B2C5E] opacity-20 blur-3xl rounded-full -translate-y-1/2 translate-x-1/2" data-id="element-122" />

          <div className="relative z-10" data-id="element-123">
            <h3 className="text-2xl md:text-3xl font-serif text-[#C9A96E] mb-6" data-id="element-124">
              La verdad que cambia todo
            </h3>
            <p className="text-gray-300 mb-6 text-lg" data-id="element-125">
              La mayoría de los problemas típicos (levantamientos, terminación
              irregular, tiempos eternos) no son “porque tu producto es malo”.
              Casi siempre vienen de:
            </p>

            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="element-126">
              {['Preparación incompleta (o agresiva)', 'Estructura sin criterio (perfil / apex)', 'Torno y fresas mal usadas', 'Sellado y acabado sin estándar'].map((item, idx) => <li key={idx} className="flex items-center gap-3" data-id="element-127">
                  <div className="w-2 h-2 bg-[#C9A96E] rounded-full" data-id="element-128" />
                  <span className="text-white font-medium" data-id="element-129">{item}</span>
                </li>)}
            </ul>

            <div className="bg-white/10 p-4 rounded-lg text-center" data-id="element-130">
              <p className="text-xl" data-id="element-131">
                O sea: no te falta “más ganas”.{' '}
                <span className="text-[#C9A96E] font-bold" data-id="element-132">
                  Te falta método profesional.
                </span>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}